import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, ClickAwayListener, Paper, Popper } from "@mui/material";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";

const presetTimes = [
  "08:00", "08:30", "09:00", "09:30", "10:00", "10:30",
  "11:00", "11:30", "12:00", "12:30", "13:00", "13:30",
  "14:00", "14:30", "15:00", "15:30", "16:00", "16:30",
  "17:00", "17:30", "18:00", "18:30", "19:00",
];

const DOW = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function pad(n) { return String(n).padStart(2, "0"); }

function parseValue(value) {
  if (!value) return null;
  const [d, t] = String(value).split("T");
  if (!d) return null;
  const [y, mo, da] = d.split("-").map(Number);
  const [h, mi] = (t || "00:00").split(":").map(Number);
  if (!y || !mo || !da) return null;
  return new Date(y, mo - 1, da, h || 0, mi || 0);
}

function toValue(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function toDateValue(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function formatTimeLabel(time) {
  const [h, m] = time.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const display = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${display}:${m} ${ampm}`;
}

function formatDisplay(date) {
  const month = MONTHS[date.getMonth()].slice(0, 3);
  const hour = date.getHours();
  const ampm = hour >= 12 ? "PM" : "AM";
  const display = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${month} ${date.getDate()}, ${date.getFullYear()} · ${display}:${pad(date.getMinutes())} ${ampm}`;
}

function formatDateDisplay(date) {
  const month = MONTHS[date.getMonth()].slice(0, 3);
  return `${month} ${date.getDate()}, ${date.getFullYear()}`;
}

function buildCalendar(viewDate) {
  const first = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - offset);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

const navBtnSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "28px",
  height: "28px",
  borderRadius: "calc(0.75rem - 2px)",
  border: "none",
  backgroundColor: "transparent",
  color: "hsl(150 30% 12%)",
  cursor: "pointer",
  transition: "background-color 0.2s",
  "&:hover": { backgroundColor: "hsl(140 8% 90%)" },
};

const DateTimeSelect = ({ value, onChange, placeholder = "Pick a date & time…", dateOnly = false }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const selected = useMemo(() => parseValue(value), [value]);
  const [viewDate, setViewDate] = useState(() => selected || new Date());

  useEffect(() => {
    if (selected) setViewDate(selected);
  }, [value]);

  const days = useMemo(() => buildCalendar(viewDate), [viewDate]);
  const today = new Date();
  const selectedTime = selected ? `${pad(selected.getHours())}:${pad(selected.getMinutes())}` : null;

  const updateDate = (d) => {
    if (dateOnly) {
      onChange(toDateValue(d));
      setOpen(false);
      return;
    }
    const base = selected || new Date();
    const next = new Date(d.getFullYear(), d.getMonth(), d.getDate(), base.getHours(), base.getMinutes());
    onChange(toValue(next));
  };

  const updateTime = (timeStr) => {
    const [h, m] = timeStr.split(":").map(Number);
    const base = selected || new Date();
    const next = new Date(base.getFullYear(), base.getMonth(), base.getDate(), h, m);
    onChange(toValue(next));
  };

  return (
    <>
      <Box
        component="button"
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "8px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          borderRadius: "calc(0.75rem - 2px)",
          fontSize: "14px",
          fontWeight: 400,
          fontFamily: "Segoe UI Regular, system-ui, sans-serif",
          transition: "color 0.2s, background-color 0.2s",
          border: "1px solid hsl(140 8% 88%)",
          backgroundColor: "var(--input-bg, hsl(140 10% 95%))",
          height: "40px",
          px: "16px",
          py: "8px",
          width: "100%",
          minWidth: 0,
          textAlign: "left",
          color: selected ? "hsl(150 30% 12%)" : "hsl(150 10% 46%)",
          cursor: "pointer",
          boxSizing: "border-box",
          "&:hover": {
            backgroundColor: "hsl(80 97% 55%)",
            color: "hsl(150 38% 12%)",
          },
          "&:focus-visible": {
            outline: "none",
            boxShadow: "0 0 0 2px hsl(140 10% 95%), 0 0 0 4px hsl(150 38% 17%)",
          },
          "&:disabled": {
            pointerEvents: "none",
            opacity: 0.5,
          },
        }}
      >
        <Calendar size={16} style={{ color: "hsl(150, 10%, 46%)", flexShrink: 0 }} />
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
          {selected ? (dateOnly ? formatDateDisplay(selected) : formatDisplay(selected)) : placeholder}
        </Box>
      </Box>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        modifiers={[
          { name: "offset", options: { offset: [0, 6] } },
          { name: "flip", enabled: true, options: { fallbackPlacements: ["bottom-end", "top-start", "top-end"] } },
          { name: "preventOverflow", options: { padding: 8 } },
        ]}
        sx={{ zIndex: 1500 }}
      >
        <ClickAwayListener
          onClickAway={(e) => {
            if (anchorRef.current && anchorRef.current.contains(e.target)) return;
            setOpen(false);
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "auto",
              maxWidth: "calc(100vw - 2rem)",
              borderRadius: "calc(0.75rem - 2px)",
              border: "1px solid hsl(140 8% 88%)",
              p: "12px",
              color: "hsl(150 30% 12%)",
              boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
              display: "flex",
              gap: "12px",
              alignItems: "stretch",
            }}
          >
            <Box sx={{ width: "232px", flexShrink: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "8px" }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}
                  sx={navBtnSx}
                >
                  <ChevronLeft size={16} />
                </Box>
                <Box sx={{ fontSize: "13px", fontWeight: 600, fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                  {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}
                  sx={navBtnSx}
                >
                  <ChevronRight size={16} />
                </Box>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px", mb: "4px" }}>
                {DOW.map((d) => (
                  <Box
                    key={d}
                    sx={{
                      textAlign: "center",
                      fontSize: "11px",
                      fontWeight: 600,
                      fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                      color: "hsl(150 10% 46%)",
                      py: "4px",
                    }}
                  >
                    {d}
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px" }}>
                {days.map((d, idx) => {
                  const inMonth = d.getMonth() === viewDate.getMonth();
                  const isToday = sameDay(d, today);
                  const isSelected = selected && sameDay(d, selected);
                  return (
                    <Box
                      key={idx}
                      component="button"
                      type="button"
                      onClick={() => updateDate(d)}
                      sx={{
                        fontSize: "12px",
                        fontWeight: isToday ? 700 : 500,
                        fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                        height: "28px",
                        borderRadius: "calc(0.75rem - 2px)",
                        border: "none",
                        cursor: "pointer",
                        backgroundColor: isSelected ? "hsl(150 38% 17%)" : "transparent",
                        color: isSelected
                          ? "hsl(80 97% 72%)"
                          : inMonth
                            ? "hsl(150 30% 12%)"
                            : "hsl(150 10% 70%)",
                        transition: "color 0.2s, background-color 0.2s",
                        "&:hover": isSelected ? {} : { backgroundColor: "hsl(140 8% 90%)" },
                      }}
                    >
                      {d.getDate()}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {!dateOnly && (
              <>
                <Box sx={{ width: "1px", bgcolor: "hsl(140 8% 88%)", flexShrink: 0 }} />

                <Box
                  sx={{
                    width: "92px",
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    maxHeight: "248px",
                    overflowY: "auto",
                    pr: "2px",
                  }}
                >
                  {presetTimes.map((t) => {
                    const isSel = selectedTime === t;
                    return (
                      <Box
                        key={t}
                        component="button"
                        type="button"
                        onClick={() => updateTime(t)}
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                          px: "8px",
                          py: "6px",
                          borderRadius: "calc(0.75rem - 2px)",
                          transition: "color 0.2s, background-color 0.2s",
                          textAlign: "center",
                          cursor: "pointer",
                          border: "none",
                          flexShrink: 0,
                          backgroundColor: isSel ? "hsl(150 38% 17%)" : "transparent",
                          color: isSel ? "hsl(80 97% 72%)" : "hsl(150 30% 12%)",
                          "&:hover": isSel ? {} : { backgroundColor: "hsl(140 8% 90%)" },
                        }}
                      >
                        {formatTimeLabel(t)}
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default DateTimeSelect;
